import { Position } from '@turf/turf';
import {
  MIN_RING_VERTICES,
  isPositiveNumber,
} from 'app/utils/field-boundary/shared';
import {
  ImplementStep,
  IsobusStage,
  LatLngPoint,
} from 'app/utils/field-boundary/types';

/**
 * Trail keys holding the whole coverage of an NVG task (IsoView/Agronave), in
 * order of preference. They share the same polygons and differ only in the
 * value used to color them; `inst_rate` comes first because its value tells
 * applying apart from driving around with the sections on.
 */
const NVG_COVERAGE_TRAIL_KEYS = ['inst_rate', 'speed'];

/** Overlap trails only repeat parts of the coverage, so they are skipped. */
const OVERLAP_TRAIL_KEY_PREFIX = 'overlap';

/** ISOBUS trails nest polygons as `[{ device: { key: [trails, unit] } }]`. */
const MAX_TRAIL_NESTING_DEPTH = 8;

/**
 * Meters per unit of the ISOBUS implement widths. The parser converts `mm`
 * and `cm` to meters already; they are accepted in case it stops doing so.
 */
const METERS_PER_WIDTH_UNIT = new Map<unknown, number>([
  ['m', 1],
  ['cm', 0.01],
  ['mm', 0.001],
]);

const MAX_LATITUDE = 90;
const MAX_LONGITUDE = 180;

interface CoverageTrail {
  paths: LatLngPoint[];
  valueOperation?: unknown;
}

/**
 * Valid coordinates only. (0, 0) is what receivers report without a fix; a
 * single such point would stretch the raster across the ocean.
 */
function isValidPoint(point: LatLngPoint | null | undefined): boolean {
  return (
    !!point &&
    Number.isFinite(point.lat) &&
    Number.isFinite(point.lng) &&
    Math.abs(point.lat) <= MAX_LATITUDE &&
    Math.abs(point.lng) <= MAX_LONGITUDE &&
    !(point.lat === 0 && point.lng === 0)
  );
}

function samePoint(a: LatLngPoint, b: LatLngPoint): boolean {
  return a.lat === b.lat && a.lng === b.lng;
}

/** Drops invalid points and consecutive duplicates, including a closing point. */
export function sanitizePath(
  path: LatLngPoint[] | null | undefined,
): LatLngPoint[] {
  const cleaned: LatLngPoint[] = [];
  if (Array.isArray(path)) {
    path.forEach((point) => {
      if (isValidPoint(point)) {
        const isDuplicate =
          cleaned.length > 0 && samePoint(cleaned[cleaned.length - 1], point);
        if (!isDuplicate) {
          cleaned.push({ lat: point.lat, lng: point.lng });
        }
      }
    });
  }
  if (
    cleaned.length > 1 &&
    samePoint(cleaned[0], cleaned[cleaned.length - 1])
  ) {
    cleaned.pop();
  }
  return cleaned;
}

/** GeoJSON ring ([lng, lat], closed) of a path, or `null` when degenerate. */
export function toClosedRing(
  path: LatLngPoint[] | null | undefined,
): Position[] | null {
  const cleaned = sanitizePath(path);
  if (cleaned.length < MIN_RING_VERTICES) {
    return null;
  }
  const ring: Position[] = cleaned.map((point) => [point.lng, point.lat]);
  ring.push(ring[0]);
  return ring;
}

function findNvgCoverageKey(trails: unknown): string | undefined {
  if (!trails || typeof trails !== 'object' || Array.isArray(trails)) {
    return undefined;
  }
  const record = trails as Record<string, unknown>;
  return NVG_COVERAGE_TRAIL_KEYS.find((key) => {
    const value = record[key];
    return Array.isArray(value) && value.length > 0;
  });
}

function isTrailPolygon(node: object): node is CoverageTrail {
  const paths = (node as { paths?: unknown }).paths;
  return Array.isArray(paths) && paths.length >= MIN_RING_VERTICES;
}

function hasApplication(trail: CoverageTrail): boolean {
  const value = Number(trail.valueOperation);
  return Number.isFinite(value) && value > 0;
}

function collectTrailPolygonsInto(
  node: unknown,
  trails: CoverageTrail[],
  depth: number,
): void {
  if (!node || typeof node !== 'object' || depth > MAX_TRAIL_NESTING_DEPTH) {
    return;
  }
  if (isTrailPolygon(node)) {
    trails.push(node);
  } else if (Array.isArray(node)) {
    node.forEach((child) => collectTrailPolygonsInto(child, trails, depth + 1));
  } else {
    Object.entries(node).forEach(([key, child]) => {
      if (!key.startsWith(OVERLAP_TRAIL_KEY_PREFIX)) {
        collectTrailPolygonsInto(child, trails, depth + 1);
      }
    });
  }
}

/**
 * Collects the polygons worked in a task, accepting both trail layouts:
 * NVG (`{ inst_rate: Trail[], speed: Trail[], ... }`) and ISOBUS
 * (`[{ device: { key: [Trail[], unit] } }]`). Overlap trails are ignored.
 *
 * @remarks
 * Trails without a positive value are dropped as long as some trail has one:
 * the sections were on but nothing was applied, which is what driving to the
 * field looks like. Operations that record no rate at all keep every trail.
 */
export function collectCoveragePolygons(trails: unknown): LatLngPoint[][] {
  const collected: CoverageTrail[] = [];
  const nvgCoverageKey = findNvgCoverageKey(trails);

  if (nvgCoverageKey) {
    collectTrailPolygonsInto(
      (trails as Record<string, unknown>)[nvgCoverageKey],
      collected,
      0,
    );
  } else {
    collectTrailPolygonsInto(trails, collected, 0);
  }

  let worked = collected;
  const applied = collected.filter(hasApplication);
  if (applied.length > 0) {
    worked = applied;
  }
  return worked.map((trail) => trail.paths);
}

function stepImplementWidth(
  step: ImplementStep | null | undefined,
): number | null {
  const recordedWidth = Number(step?.impl_width);
  if (isPositiveNumber(recordedWidth)) {
    return recordedWidth;
  }
  let sectionsWidth = 0;
  if (Array.isArray(step?.sec_width_meters)) {
    step.sec_width_meters.forEach((sectionWidth) => {
      const width = Number(sectionWidth);
      if (isPositiveNumber(width)) {
        sectionsWidth += width;
      }
    });
  }
  if (sectionsWidth > 0) {
    return sectionsWidth;
  }
  return null;
}

function widest(widths: (number | null)[]): number | null {
  let result: number | null = null;
  widths.forEach((width) => {
    if (width !== null && (result === null || width > result)) {
      result = width;
    }
  });
  return result;
}

/**
 * Width of the widest implement used in NVG task steps: `impl_width` when it
 * was recorded, otherwise the sum of the section widths (newer AGP layouts
 * leave `impl_width` empty). `null` when no step tells.
 */
export function implementWidthFromSteps(
  steps: ImplementStep[] | null | undefined,
): number | null {
  if (!Array.isArray(steps)) {
    return null;
  }
  return widest(steps.map(stepImplementWidth));
}

function ecuImplementWidth(ecu: unknown): number | null {
  const implementWidth = (ecu as { implementWidth?: unknown } | null)
    ?.implementWidth;
  if (!Array.isArray(implementWidth)) {
    return null;
  }
  const [value, unit] = implementWidth;
  const meters = Number(value) * METERS_PER_WIDTH_UNIT.get(unit);
  if (isPositiveNumber(meters)) {
    return meters;
  }
  return null;
}

/**
 * Width of the widest implement in ISOBUS task stages, read from each
 * device's `implementWidth` (`[value, unit]`). `null` when no stage tells.
 */
export function implementWidthFromIsobusStages(
  stages: IsobusStage[] | null | undefined,
): number | null {
  if (!Array.isArray(stages)) {
    return null;
  }
  const widths: (number | null)[] = [];
  stages.forEach((stage) => {
    const ecusData = stage?.ecusData;
    if (ecusData && typeof ecusData === 'object') {
      Object.values(ecusData).forEach((ecu) =>
        widths.push(ecuImplementWidth(ecu)),
      );
    }
  });
  return widest(widths);
}
